import {
  type BaseFieldProps,
  Field,
  FieldContent,
  FieldDescriptionAndValidation,
  FieldLabel,
  FieldValidation,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  labelToString,
} from '@glade-ai/ui';
import { type SelectProps } from '@radix-ui/react-select';
import { type FocusEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { REACT_SELECT_SUPPORTED_COUNTRIES } from '@/helpers/supported-creator-countries';

type CountryFieldProps = Omit<SelectProps, 'children'> & BaseFieldProps;

/** A form field for selecting a country from a dropdown list. */
const CountryField = ({
  field: { onChange, onBlur, value, name, disabled, ref },
  fieldState,
  label = 'forms.inputs.country.label',
  ...selectProps
}: CountryFieldProps) => {
  const { t } = useTranslation();

  return (
    <Field validationValence={fieldState.invalid ? 'error' : 'neutral'}>
      <FieldContent>
        <FieldLabel>{labelToString(t, label)}</FieldLabel>
        <Select {...selectProps} disabled={disabled} name={name} value={value as string} onValueChange={onChange}>
          <SelectTrigger onBlur={onBlur as FocusEventHandler<HTMLButtonElement>} className='text-start' ref={ref}>
            <SelectValue className='grow' />
          </SelectTrigger>
          <SelectContent>
            {REACT_SELECT_SUPPORTED_COUNTRIES.map((country) => (
              <SelectItem key={country.value} value={country.value}>
                {country.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldDescriptionAndValidation>
          {fieldState.invalid && <FieldValidation>{fieldState.error?.message}</FieldValidation>}
        </FieldDescriptionAndValidation>
      </FieldContent>
    </Field>
  );
};

export { CountryField };

export type { CountryFieldProps };
